import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  applyProfileWeights,
  computeWeightedScore,
  createRubricsAPI,
  suggestedPriorityFromScore,
} from '../../services/rubricsAPI';
import OrganizationRubricsTable3 from './OrganizationRubricsTable3';
import { ContactsForm } from '../forms';
import API_BASE_URL from '../../config/api';
import { useBusinessUnitApi } from '../../contexts/BusinessUnitContext';

const EMPTY_FORM = {
  profile_id: '',
  contact_id: '',
  contact_name: '',
  contact_role: '',
  confidence_score: '',
  observed_score: '',
  notes: '',
  assessed_at: '',
  scores: {},
};

const EMPTY_CONTACT_FORM = {
  contact_code: 'Se asigna automáticamente',
  full_name: '',
  role: '',
  email: '',
  linkedin: '',
  phone: '',
  organization_id: '',
  status: 'Activo',
  source: '',
  source_details: '',
};

const SCORE_LEVELS = [1, 2, 3, 4, 5];

const formatDateInput = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const toOptionalNumber = (value) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const scoreDescriptor = (criterion, score) => criterion?.[`score_${score}`] || '';

const RubricFormModal = ({
  isOpen,
  onClose,
  isDarkMode,
  organizationId,
  organizationName,
  contacts = [],
  baseCriteria = [],
  profiles = [],
  initialForm,
  isEditing,
  isSaving,
  formError,
  onSave,
  onContactsChanged,
}) => {
  const { authFetch } = useBusinessUnitApi();
  const [form, setForm] = useState(EMPTY_FORM);
  const [extraContacts, setExtraContacts] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState(EMPTY_CONTACT_FORM);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactFormError, setContactFormError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm(initialForm || EMPTY_FORM);
      setExtraContacts([]);
      setShowContactForm(false);
      setContactFormError('');
    }
  }, [isOpen, initialForm]);

  const selectedProfile = useMemo(
    () => profiles.find((p) => String(p.id) === String(form.profile_id)) || profiles.find((p) => p.is_default) || profiles[0] || null,
    [profiles, form.profile_id]
  );

  const criteria = useMemo(
    () => applyProfileWeights(baseCriteria, selectedProfile),
    [baseCriteria, selectedProfile]
  );

  const totalWeight = useMemo(
    () => criteria.reduce((acc, c) => acc + (Number(c.weight_pct) || 0), 0),
    [criteria]
  );

  const weighted = computeWeightedScore(criteria, form.scores);
  const priority = suggestedPriorityFromScore(weighted);

  const allContacts = useMemo(() => {
    const byId = new Map();
    [...(contacts || []), ...extraContacts].forEach((item) => {
      if (item?.id == null) return;
      byId.set(String(item.id), item);
    });
    return Array.from(byId.values());
  }, [contacts, extraContacts]);

  const organizationOptions = useMemo(
    () =>
      organizationId
        ? [{ value: String(organizationId), label: organizationName || 'Organización' }]
        : [],
    [organizationId, organizationName]
  );

  if (!isOpen) return null;

  const colors = isDarkMode
    ? {
        overlay: 'rgba(0,0,0,0.55)',
        panel: '#18181b',
        border: '#2f2f35',
        sectionBorder: '#2a2a31',
        text: '#d4d4d8',
        title: '#f4f4f5',
        muted: '#a1a1aa',
        inputBg: '#141417',
        sectionBg: '#141417',
        accent: '#16a34a',
        headerBg: '#1f1f23',
        rowAlt: '#121215',
        scoreCell: 'rgba(22,163,74,0.18)',
        scoreBorder: '#16a34a',
        metaBg: '#1c1c20',
      }
    : {
        overlay: 'rgba(15,23,42,0.35)',
        panel: '#ffffff',
        border: '#d1d5db',
        sectionBorder: '#e5e7eb',
        text: '#1f2937',
        title: '#111827',
        muted: '#6b7280',
        inputBg: '#ffffff',
        sectionBg: '#f9fafb',
        accent: '#16a34a',
        headerBg: '#f3f4f6',
        rowAlt: '#fafafa',
        scoreCell: '#dcfce7',
        scoreBorder: '#86efac',
        metaBg: '#f8fafc',
      };

  const inputStyle = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    height: '32px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    padding: '0 8px',
    fontSize: '12px',
  };

  const handleContactPick = (contactId) => {
    if (!contactId) {
      setForm((prev) => ({ ...prev, contact_id: '' }));
      return;
    }
    const contact = allContacts.find((item) => String(item.id) === String(contactId));
    if (!contact) return;
    setForm((prev) => ({
      ...prev,
      contact_id: String(contact.id),
      contact_name: contact.full_name || prev.contact_name,
      contact_role: contact.role || prev.contact_role,
    }));
  };

  const openCreateContact = () => {
    if (!organizationId) return;
    setContactFormError('');
    setContactFormData({
      ...EMPTY_CONTACT_FORM,
      organization_id: String(organizationId),
    });
    setShowContactForm(true);
  };

  const handleSaveContact = async () => {
    if (!String(contactFormData.full_name || '').trim()) {
      setContactFormError('El nombre del contacto es obligatorio.');
      return;
    }
    if (!organizationId) {
      setContactFormError('No hay una organización seleccionada.');
      return;
    }
    setIsSavingContact(true);
    setContactFormError('');
    try {
      const payload = {
        full_name: String(contactFormData.full_name || '').trim(),
        role: String(contactFormData.role || '').trim(),
        email: String(contactFormData.email || '').trim(),
        linkedin: String(contactFormData.linkedin || '').trim(),
        phone: String(contactFormData.phone || '').trim(),
        organization_id: Number(organizationId),
        status: contactFormData.status || 'Activo',
        source: String(contactFormData.source || '').trim(),
        source_details: String(contactFormData.source_details || '').trim(),
      };
      const response = await authFetch(`${API_BASE_URL}/api/contacts/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      const created = await response.json();
      setExtraContacts((prev) => [...prev, created]);
      setForm((prev) => ({
        ...prev,
        contact_id: created?.id ? String(created.id) : prev.contact_id,
        contact_name: created?.full_name || prev.contact_name,
        contact_role: created?.role || prev.contact_role,
      }));
      setShowContactForm(false);
      onContactsChanged?.();
    } catch (error) {
      setContactFormError(error.message || 'No se pudo crear el contacto.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const setCriterionScore = (criterionId, value) => {
    setForm((prev) => ({
      ...prev,
      scores: { ...prev.scores, [criterionId]: value },
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave?.(form, weighted, priority, selectedProfile);
  };

  const thStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    padding: '8px 6px',
    fontSize: '11px',
    fontWeight: 700,
    color: colors.title,
    textAlign: 'center',
    background: colors.headerBg,
    borderBottom: `1px solid ${colors.border}`,
    borderRight: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '6px',
    borderBottom: `1px solid ${colors.border}`,
    borderRight: `1px solid ${colors.border}`,
    verticalAlign: 'top',
    fontSize: '11px',
    lineHeight: 1.35,
    color: colors.text,
  };

  return (
    <>
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        background: colors.overlay,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: 'min(1480px, 98vw)',
          maxHeight: '94vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: colors.panel,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '14px',
          color: colors.text,
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0, color: colors.title, fontSize: '17px' }}>
              Rúbrica de evaluación por empresa
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: colors.muted }}>
              {isEditing ? 'Editar evaluación' : 'Nueva evaluación'} · {organizationName || 'Organización'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: '32px',
              borderRadius: '8px',
              border: `1px solid ${colors.sectionBorder}`,
              background: 'transparent',
              color: colors.text,
              cursor: 'pointer',
              padding: '0 12px',
              fontSize: '13px',
            }}
          >
            Cerrar
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
            gap: '10px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              border: `1px solid ${colors.sectionBorder}`,
              borderRadius: '10px',
              background: colors.metaBg,
              padding: '10px 12px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '8px 12px',
            }}
          >
            <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
              Prefill contacto
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select
                  value={form.contact_id || ''}
                  onChange={(e) => handleContactPick(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">Seleccionar…</option>
                  {allContacts.map((contact) => (
                    <option key={String(contact.id)} value={String(contact.id)}>
                      {contact.full_name || contact.contact_code || `CTC ${contact.id}`}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={openCreateContact}
                  disabled={!organizationId}
                  title={organizationId ? 'Nuevo contacto' : 'Selecciona una organización primero'}
                  aria-label="Nuevo contacto"
                  style={{
                    flexShrink: 0,
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.inputBg,
                    color: colors.text,
                    cursor: organizationId ? 'pointer' : 'not-allowed',
                    opacity: organizationId ? 1 : 0.45,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <Plus size={16} />
                </button>
              </div>
            </label>
            <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
              Perfil de comercialización
              <select
                value={form.profile_id}
                onChange={(e) => setForm((prev) => ({ ...prev, profile_id: e.target.value }))}
                style={inputStyle}
              >
                {profiles.map((profile) => (
                  <option key={String(profile.id)} value={String(profile.id)}>
                    {profile.name}
                    {profile.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
              Contacto
              <input
                value={form.contact_name}
                onChange={(e) => setForm((prev) => ({ ...prev, contact_name: e.target.value }))}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
              Empresa
              <input value={organizationName || ''} readOnly style={{ ...inputStyle, opacity: 0.85 }} />
            </label>
            <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
              Cargo
              <input
                value={form.contact_role}
                onChange={(e) => setForm((prev) => ({ ...prev, contact_role: e.target.value }))}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
              Última actualización
              <input
                type="date"
                value={form.assessed_at}
                onChange={(e) => setForm((prev) => ({ ...prev, assessed_at: e.target.value }))}
                style={inputStyle}
              />
            </label>
          </div>

          <div
            style={{
              border: `1px solid ${colors.sectionBorder}`,
              borderRadius: '10px',
              background: colors.metaBg,
              padding: '10px 12px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '8px',
            }}
          >
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '8px 10px', background: colors.panel }}>
              <div style={{ fontSize: '11px', color: colors.muted }}>Score ponderado</div>
              <div style={{ marginTop: 2, fontSize: '22px', fontWeight: 800, color: colors.accent }}>
                {weighted == null ? '—' : weighted.toFixed(2).replace('.', ',')}
              </div>
            </div>
            <label style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '8px 10px', background: colors.panel, display: 'grid', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: colors.muted }}>Confianza puntaje</span>
              <select
                value={form.confidence_score}
                onChange={(e) => setForm((prev) => ({ ...prev, confidence_score: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Sin asignar</option>
                {SCORE_LEVELS.map((n) => (
                  <option key={n} value={String(n)}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '8px 10px', background: colors.panel }}>
              <div style={{ fontSize: '11px', color: colors.muted }}>Prioridad sugerida</div>
              <div style={{ marginTop: 4, fontSize: '14px', fontWeight: 700, color: colors.title }}>
                {priority || '—'}
              </div>
            </div>
            <label style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '8px 10px', background: colors.panel, display: 'grid', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: colors.muted }}>Score Real</span>
              <input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={form.observed_score}
                onChange={(e) => setForm((prev) => ({ ...prev, observed_score: e.target.value }))}
                style={inputStyle}
              />
            </label>
          </div>
        </div>

        {selectedProfile?.description ? (
          <p style={{ margin: 0, fontSize: '12px', color: colors.muted, flexShrink: 0 }}>
            Pesos del perfil <strong>{selectedProfile.name}</strong>: {selectedProfile.description}
          </p>
        ) : null}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            border: `1px solid ${colors.sectionBorder}`,
            borderRadius: '10px',
            overflow: 'auto',
            background: colors.panel,
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: 150 }}>Criterio</th>
                <th style={{ ...thStyle, minWidth: 56 }}>Prefijo</th>
                <th style={{ ...thStyle, minWidth: 70 }}>Peso (%)</th>
                <th style={{ ...thStyle, minWidth: 78, background: colors.scoreCell }}>Puntaje</th>
                {SCORE_LEVELS.map((n) => (
                  <th key={n} style={{ ...thStyle, minWidth: 140 }}>
                    Puntaje {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criteria.map((criterion, index) => {
                const selected = form.scores[criterion.id] ?? '';
                return (
                  <tr key={criterion.id} style={{ background: index % 2 ? colors.rowAlt : colors.panel }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: colors.title }}>{criterion.name}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>{criterion.code}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {Number(criterion.weight_pct || 0).toLocaleString('es-CL')}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'center',
                        background: colors.scoreCell,
                        borderColor: colors.scoreBorder,
                      }}
                    >
                      <select
                        value={selected}
                        onChange={(e) => setCriterionScore(criterion.id, e.target.value)}
                        style={{
                          ...inputStyle,
                          height: '30px',
                          textAlign: 'center',
                          fontWeight: 700,
                          background: colors.panel,
                          borderColor: colors.scoreBorder,
                        }}
                      >
                        <option value="">—</option>
                        {SCORE_LEVELS.map((n) => (
                          <option key={n} value={String(n)}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </td>
                    {SCORE_LEVELS.map((n) => {
                      const isActive = String(selected) === String(n);
                      return (
                        <td
                          key={n}
                          style={{
                            ...tdStyle,
                            background: isActive ? colors.scoreCell : undefined,
                            cursor: 'pointer',
                          }}
                          title={`Asignar puntaje ${n}`}
                          onClick={() => setCriterionScore(criterion.id, String(n))}
                        >
                          {scoreDescriptor(criterion, n) || '—'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: colors.headerBg }}>
                <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                  Resumen
                </td>
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>
                  {totalWeight.toLocaleString('es-CL')}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    textAlign: 'center',
                    fontWeight: 800,
                    color: colors.accent,
                    background: colors.scoreCell,
                    fontSize: '13px',
                  }}
                >
                  {weighted == null ? '—' : weighted.toFixed(2).replace('.', ',')}
                </td>
                <td style={{ ...tdStyle, color: colors.muted }} colSpan={5}>
                  Score ponderado = Σ (puntaje × peso) / Σ pesos asignados. Solo se guardan los puntajes 1–5.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div
          style={{
            flexShrink: 0,
            border: `1px solid ${colors.sectionBorder}`,
            borderRadius: '10px',
            background: colors.sectionBg,
            padding: '10px 12px',
            display: 'grid',
            gap: '8px',
          }}
        >
          <label style={{ display: 'grid', gap: '3px', fontSize: '11px', color: colors.muted }}>
            Notas generales
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={2}
              style={{
                ...inputStyle,
                height: 'auto',
                padding: '8px',
                resize: 'vertical',
              }}
            />
          </label>

          {formError ? <p style={{ margin: 0, color: '#dc2626', fontSize: '13px' }}>{formError}</p> : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              style={{
                height: '34px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.panel,
                color: colors.text,
                cursor: 'pointer',
                padding: '0 14px',
                fontSize: '13px',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              style={{
                height: '34px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                cursor: isSaving ? 'wait' : 'pointer',
                padding: '0 16px',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {isSaving ? 'Guardando…' : isEditing ? 'Guardar cambios' : 'Crear rúbrica'}
            </button>
          </div>
        </div>
      </form>
    </div>
      <ContactsForm
        isOpen={showContactForm}
        onClose={() => {
          if (!isSavingContact) {
            setShowContactForm(false);
            setContactFormError('');
          }
        }}
        onSave={handleSaveContact}
        isSaving={isSavingContact}
        isDarkMode={isDarkMode}
        isEditing={false}
        formData={contactFormData}
        onChange={(field, value) => setContactFormData((prev) => ({ ...prev, [field]: value }))}
        formError={contactFormError}
        organizationOptions={organizationOptions}
        zIndex={14000}
      />
    </>
  );
};

const OrganizationRubricsSection = ({
  organization,
  contacts = [],
  isDarkMode = false,
  onContactsChanged,
}) => {
  const organizationId = organization?.id;
  const { apiClient } = useBusinessUnitApi();
  const rubricsAPI = useMemo(() => createRubricsAPI(apiClient), [apiClient]);
  const [criteria, setCriteria] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formSeed, setFormSeed] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [listError, setListError] = useState('');

  const defaultProfileId = useMemo(() => {
    const preferred = profiles.find((p) => p.is_default) || profiles[0];
    return preferred ? String(preferred.id) : '';
  }, [profiles]);

  const loadRubrics = useCallback(async () => {
    if (!organizationId) {
      setRubrics([]);
      return;
    }
    setLoading(true);
    setListError('');
    try {
      const rows = await rubricsAPI.listByOrganization(organizationId);
      setRubrics(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading rubrics:', error);
      setRubrics([]);
      setListError(error.message || 'No se pudieron cargar las rúbricas.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, rubricsAPI]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [criteriaResult, profilesResult] = await Promise.allSettled([
        rubricsAPI.getCriteria(),
        rubricsAPI.getProfiles(),
      ]);
      if (cancelled) return;
      if (criteriaResult.status === 'fulfilled') {
        setCriteria(Array.isArray(criteriaResult.value) ? criteriaResult.value : []);
      } else {
        console.error('Error loading rubric criteria:', criteriaResult.reason);
        setCriteria([]);
        setListError(criteriaResult.reason?.message || 'No se pudieron cargar los criterios de rúbrica.');
      }
      if (profilesResult.status === 'fulfilled') {
        setProfiles(Array.isArray(profilesResult.value) ? profilesResult.value : []);
      } else {
        console.error('Error loading rubric profiles:', profilesResult.reason);
        // Criteria alone are enough; profile selector falls back to catalog weights.
        setProfiles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rubricsAPI]);

  useEffect(() => {
    loadRubrics();
  }, [loadRubrics]);

  const openCreate = () => {
    setEditingId(null);
    setFormError('');
    setFormSeed({
      ...EMPTY_FORM,
      profile_id: defaultProfileId,
      assessed_at: new Date().toISOString().slice(0, 10),
      scores: Object.fromEntries(criteria.map((c) => [c.id, ''])),
    });
    setShowForm(true);
  };

  const openEdit = async (row) => {
    if (!row?.id) return;
    setFormError('');
    try {
      const detail = await rubricsAPI.getById(row.id);
      const scores = Object.fromEntries(criteria.map((c) => [c.id, '']));
      (detail.scores || []).forEach((item) => {
        scores[item.criterion_id] = item.score == null ? '' : String(item.score);
      });
      setEditingId(detail.id);
      const matchedContact = (contacts || []).find(
        (item) =>
          String(item.full_name || '').trim().toLowerCase() ===
          String(detail.contact_name || '').trim().toLowerCase()
      );
      setFormSeed({
        profile_id: detail.profile_id ? String(detail.profile_id) : defaultProfileId,
        contact_id: matchedContact?.id ? String(matchedContact.id) : '',
        contact_name: detail.contact_name || '',
        contact_role: detail.contact_role || '',
        confidence_score: detail.confidence_score == null ? '' : String(Number(detail.confidence_score)),
        observed_score: detail.observed_score == null ? '' : String(Number(detail.observed_score)),
        notes: detail.notes || '',
        assessed_at: formatDateInput(detail.assessed_at),
        scores,
      });
      setShowForm(true);
    } catch (error) {
      setListError(error.message || 'No se pudo abrir la rúbrica.');
    }
  };

  const handleDelete = async (rows) => {
    const selected = Array.isArray(rows) ? rows : rows ? [rows] : [];
    if (!selected.length) return;
    const confirmed = window.confirm(
      selected.length === 1 ? '¿Eliminar esta rúbrica?' : `¿Eliminar ${selected.length} rúbricas?`
    );
    if (!confirmed) return;
    try {
      await Promise.all(selected.map((row) => rubricsAPI.remove(row.id)));
      await loadRubrics();
    } catch (error) {
      setListError(error.message || 'No se pudieron eliminar las rúbricas.');
    }
  };

  const handleSave = async (form) => {
    if (!organizationId) return;
    setIsSaving(true);
    setFormError('');
    try {
      const payload = {
        organization_id: Number(organizationId),
        profile_id: (() => {
          const raw = form.profile_id ? Number(form.profile_id) : null;
          return raw && raw > 0 ? raw : null;
        })(),
        contact_name: form.contact_name || '',
        contact_role: form.contact_role || '',
        confidence_score: toOptionalNumber(form.confidence_score),
        observed_score: toOptionalNumber(form.observed_score),
        notes: form.notes || '',
        assessed_at: form.assessed_at || null,
        scores: criteria.map((criterion) => ({
          criterion_id: criterion.id,
          score: toOptionalNumber(form.scores[criterion.id]),
        })),
      };
      if (editingId) {
        await rubricsAPI.update(editingId, payload);
      } else {
        await rubricsAPI.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      await loadRubrics();
    } catch (error) {
      setFormError(error.message || 'No se pudo guardar la rúbrica.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <OrganizationRubricsTable3
        rows={rubrics}
        loading={loading}
        error={listError}
        isDarkMode={isDarkMode}
        tableTitle="4) Rúbricas"
        addButtonLabel="Nueva rúbrica"
        onAdd={openCreate}
        addDisabled={!organizationId || criteria.length === 0}
        onRowClick={openEdit}
        onBulkDelete={handleDelete}
      />

      <RubricFormModal
        isOpen={showForm}
        onClose={() => {
          if (!isSaving) {
            setShowForm(false);
            setEditingId(null);
            setFormError('');
          }
        }}
        isDarkMode={isDarkMode}
        organizationId={organizationId}
        organizationName={organization?.org_name || ''}
        contacts={contacts}
        baseCriteria={criteria}
        profiles={profiles}
        initialForm={formSeed}
        isEditing={Boolean(editingId)}
        isSaving={isSaving}
        formError={formError}
        onSave={handleSave}
        onContactsChanged={onContactsChanged}
      />
    </div>
  );
};

export default OrganizationRubricsSection;
